import { CacheCore, CacheCoreConfig, TypeCacheRecord } from "./cache-core";

export class RequestCore<
  Task extends (...args: any) => Promise<any>,
  TaskError = any,
  Meta = Record<string, any>
> extends CacheCore<
  Awaited<ReturnType<Task>> | undefined | null,
  Parameters<Task>
> {
  constructor(protected _config: TaskManagerCoreConfig<Task, TaskError, Meta>) {
    super(_config.cache ?? {});
  }

  protected _retriedOnError: number = 0;

  protected _defaultRequestDetails: RequestDataRecordType<Task> = {};

  protected _requestDetails: RequestDataRecordType<Task> =
    this._defaultRequestDetails;

  public get requestDetails(): Readonly<{
    value: RequestDataRecordType<Task>;
  }> {
    return { value: this._requestDetails };
  }

  public get meta() {
    return (this._config.meta ?? {}) as Meta;
  }

  protected _setRequestDetails(
    updater: (prev: RequestDataRecordType<Task>) => RequestDataRecordType<Task>
  ) {
    this._requestDetails = updater(this.requestDetails.value);
  }

  protected _isPrevented({
    hash,
    config: { isInvalidate = false, isPreProcess = false },
  }: {
    hash: string;
    config: RequestConfigBase<Task, TaskError, Meta>;
  }) {
    const requestDetails = this.requestDetails.value ?? {};
    const preventDuration = this._config.preventNewRequestDuration ?? 0;

    const isBlocked = Boolean(
      !isInvalidate &&
        requestDetails.createdAt &&
        !requestDetails.isFailed &&
        !requestDetails.isBlocked &&
        preventDuration > 0 &&
        this._isSameRequest(hash) &&
        Date.now() - requestDetails.createdAt <= preventDuration
    );

    if (isBlocked && !isInvalidate && !isPreProcess) {
      this._setRequestDetails((prev) => ({
        ...prev,
        isBlocked: true,
        status: "blocked",
        procedure: (prev.procedure ?? []).concat("blocked"),
      }));
    }

    return isBlocked;
  }

  protected async _requestCore({
    parameters,
    config,
  }: {
    parameters: Parameters<Task>;
    config: RequestConfigBase<Task, TaskError, Meta>;
  }): Promise<
    {
      data?: Awaited<ReturnType<Task>> | undefined | null;
      error?: TaskError;
      isError?: boolean;
      createdAt: number;
      updatedAt: number;
      hash: string;
      parameters: Parameters<Task>;
      meta: Meta;
      isPrevented?: boolean;
    } & SharedConfigs
  > {
    const createdAt = Date.now();
    const meta = this.meta;
    const isPreProcess = config.isPreProcess;
    const isInvalidate = config.isInvalidate;

    const hash = this._getHash(parameters as any);

    if (this._isPrevented({ hash, config }))
      return {
        createdAt,
        updatedAt: createdAt,
        hash,
        parameters,
        meta,
        isPrevented: true,
        isPreProcess,
        isInvalidate,
      };

    if (!config.isPreProcess && !config.isInvalidate)
      this._setRequestDetails(() => ({
        parameters,
        hash,
        createdAt,
        updatedAt: createdAt,
        isRequested: true,
        status: "requested",
        procedure: ["requested"],
      }));

    let data: Awaited<ReturnType<Task>> | undefined | null = null;
    let error: any;
    let isError: boolean | undefined;
    let isPrevented = false;

    try {
      const result = await this._try({
        parameters,
        config,
        hash,
      });

      data = result.data;
      if (result.isPrevented) isPrevented = true;
    } catch (currentError: any) {
      error = await this._error({
        parameters,
        config,
        hash,
        error: currentError,
      });
    } finally {
      if (isPrevented)
        return {
          createdAt,
          updatedAt: Date.now(),
          parameters,
          hash,
          error,
          data,
          meta,
          isPrevented,
          isInvalidate,
          isPreProcess,
          isError,
        };

      await this._finally({
        parameters,
        config,
        hash,
        error,
        data,
      });
    }

    return {
      createdAt,
      updatedAt: Date.now(),
      parameters,
      hash,
      error,
      data,
      meta,
      isPrevented,
      isInvalidate,
      isPreProcess,
      isError,
    };
  }

  private async _try({
    parameters: parametersRaw,
    config,
    hash,
  }: {
    parameters: Parameters<Task>;
    config: RequestConfigBase<Task, TaskError, Meta>;
    hash: string;
  }): Promise<{
    data: Awaited<ReturnType<Task>> | undefined | null;
    isPrevented?: boolean;
  }> {
    const meta = this.meta;

    let data: Awaited<ReturnType<Task>> | undefined | null = null;

    const { onRequest, onSuccess, onCache, isInvalidate, isPreProcess } =
      config;

    if (this._config.cache?.enableCache)
      this._getCache(hash).then(async (cacheRaw) => {
        const { cache, isIgnored = false } =
          (await this._config.interceptors?.cache?.(
            {
              cache: cacheRaw,
              hash,
              parameters: [...parametersRaw] as Parameters<Task>,
              config,
              meta,
              isInvalidate,
              isPreProcess,
            },
            this
          )) ?? { cache: cacheRaw };

        if (isIgnored) return;

        let isSameRequest = this._isSameRequest(hash);

        if (isSameRequest && !!this.requestDetails.value?.isSucceed) return;

        if (isSameRequest || isInvalidate || isPreProcess)
          await onCache?.(
            {
              cache,
              isInvalidate,
              isPreProcess,
              hash,
              parameters: parametersRaw,
              meta,
            },
            this
          );

        if (isSameRequest && !isPreProcess && !isInvalidate)
          await this._config.defaultEventHandlers?.onCache?.(
            {
              cache,
              isInvalidate,
              isPreProcess,
              hash,
              parameters: [...parametersRaw] as Parameters<Task>,
              meta,
            },
            this
          );

        if (isSameRequest && !isPreProcess && !isInvalidate)
          this._setRequestDetails((prev) => ({
            ...prev,
            isCached: true,
            status: "cached",
            updatedAt: Date.now(),
            procedure: (prev.procedure ?? []).concat("cached"),
            cache,
          }));
      });

    const { parameters, isPrevented = false } =
      (await this._config.interceptors?.request?.(
        {
          parameters: [...parametersRaw] as Parameters<Task>,
          config,
          hash,
          meta,
          isInvalidate,
          isPreProcess,
        },
        this
      )) ?? { parameters: parametersRaw };

    let isSameRequest = this._isSameRequest(hash);

    if (isSameRequest && isPrevented && !isInvalidate && !isPreProcess) {
      this._setRequestDetails((prev) => ({
        ...prev,
        isBlocked: true,
        status: "blocked",
        updatedAt: Date.now(),
        procedure: (prev.procedure ?? []).concat("blocked"),
      }));

      return { data, isPrevented: true };
    }

    if (isSameRequest || isInvalidate || isPreProcess)
      await onRequest?.(
        {
          parameters: [...parametersRaw] as Parameters<Task>,
          hash,
          isInvalidate,
          isPreProcess,
          meta,
        },
        this
      );
    if ((isSameRequest && !isPreProcess) || isInvalidate)
      await this._config.defaultEventHandlers?.onRequest?.(
        {
          parameters: [...parametersRaw] as Parameters<Task>,
          hash,
          isInvalidate,
          isPreProcess,
          meta,
        },
        this
      );

    isSameRequest = this._isSameRequest(hash);

    // const isCachePreventedRequest = this._isCachePreventNewRequest(cache);

    // data =
    //   isCachePreventedRequest && !isInvalidate
    //     ? cache?.data
    //     : await this._config.handler(parameters as any);

    // if (!isCachePreventedRequest || isInvalidate)
    //   this._setCache(paramsHash, { data, updatedAt: Date.now() });
    if (isSameRequest && !isInvalidate && !isPreProcess)
      this._setRequestDetails((prev) => ({
        ...prev,
        isRequested: true,
        status: "began",
        updatedAt: Date.now(),
        procedure: (prev.procedure ?? []).concat("began"),
      }));

    data = await this._config.handler(...(parameters ?? []));

    this._setCache(hash, { data: data ?? undefined, updatedAt: Date.now() });

    isSameRequest = this._isSameRequest(hash);

    if (isSameRequest) this._retriedOnError = 0;

    if (isSameRequest && !isInvalidate && !isPreProcess)
      this._setRequestDetails((prev) => ({
        ...prev,
        isSucceed: true,
        status: "succeed",
        updatedAt: Date.now(),
        procedure: (prev.procedure ?? []).concat("succeed"),
        data,
      }));

    const { data: interceptedData, isIgnored = false } =
      (await this._config.interceptors?.success?.(
        {
          parameters: parametersRaw,
          config,
          data,
          hash,
          meta,
          isInvalidate,
          isPreProcess,
        },
        this
      )) ?? { data };

    if (isIgnored) return { data, isPrevented: true };

    data = interceptedData;

    isSameRequest = this._isSameRequest(hash);

    if (isSameRequest || isInvalidate || isPreProcess)
      await onSuccess?.(
        {
          data,
          hash,
          isInvalidate,
          isPreProcess,
          parameters: [...parametersRaw] as Parameters<Task>,
          meta,
        },
        this
      );
    if ((isSameRequest && !isPreProcess) || isInvalidate)
      await this._config.defaultEventHandlers?.onSuccess?.(
        {
          data,
          hash,
          isInvalidate,
          isPreProcess,
          parameters: [...parametersRaw] as Parameters<Task>,
          meta,
        },
        this
      );

    return { data };
  }

  private async _error({
    parameters: parametersRaw,
    config,
    hash,
    error: errorRaw,
  }: {
    parameters: Parameters<Task>;
    config: RequestConfigBase<Task, TaskError, Meta>;
    hash: string;
    error: TaskError;
  }): Promise<TaskError> {
    const meta = this.meta;

    const parameters = [...parametersRaw] as Parameters<Task>;

    const { error, isIgnored = false } =
      (await this._config.interceptors?.error?.(
        {
          parameters,
          config,
          error: errorRaw,
          hash,
          meta,
          isInvalidate: config.isInvalidate,
          isPreProcess: config.isPreProcess,
        },
        this
      )) ?? { error: errorRaw };

    if (isIgnored) return error;

    let isSameRequest = this._isSameRequest(hash);

    if (isSameRequest && !config.isInvalidate && !config.isPreProcess)
      this._setRequestDetails((prev) => ({
        ...prev,
        isFailed: true,
        status: "failed",
        updatedAt: Date.now(),
        procedure: (prev.procedure ?? []).concat("failed"),
        error,
      }));

    if (
      this._isRetryOnError &&
      isSameRequest &&
      !config.isInvalidate &&
      !config.isPreProcess
    ) {
      ++this._retriedOnError;

      this._config.retryOnErrorDelay && this._config.retryOnErrorDelay > 0
        ? setTimeout(() => {
            this._requestCore({
              parameters,
              config,
            });
          }, this._config.retryOnErrorDelay)
        : this._requestCore({
            parameters,
            config,
          });

      return error;
    }

    if (isSameRequest || config.isPreProcess || config.isInvalidate)
      await config.onError?.(
        {
          error,
          hash,
          parameters: [...parametersRaw] as Parameters<Task>,
          isInvalidate: config.isInvalidate,
          isPreProcess: config.isPreProcess,
          meta,
        },
        this
      );
    if ((isSameRequest && !config.isPreProcess) || config.isInvalidate)
      await this._config.defaultEventHandlers?.onError?.(
        {
          error,
          hash,
          parameters: [...parametersRaw] as Parameters<Task>,
          isInvalidate: config.isInvalidate,
          isPreProcess: config.isPreProcess,
          meta,
        },
        this
      );

    return error;
  }

  private async _finally({
    parameters: parametersRaw,
    config,
    hash,
    error: errorRaw,
    data: dataRaw,
  }: {
    parameters: Parameters<Task>;
    config: RequestConfigBase<Task, TaskError, Meta>;
    hash: string;
    error: TaskError | undefined;
    data: Awaited<ReturnType<Task>> | undefined | null;
  }) {
    const meta = this.meta;

    const {
      data,
      error,
      isIgnored = false,
    } = (await this._config.interceptors?.finally?.(
      {
        parameters: [...parametersRaw] as Parameters<Task>,
        config,
        data: dataRaw,
        error: errorRaw,
        hash,
        meta,
        isInvalidate: config.isInvalidate,
        isPreProcess: config.isPreProcess,
      },
      this
    )) ?? { data: dataRaw, error: errorRaw };

    if (isIgnored) return;

    let isSameRequest = this._isSameRequest(hash);

    if (isSameRequest || config.isPreProcess || config.isInvalidate)
      await config.onFinally?.(
        {
          data,
          error,
          hash,
          parameters: [...parametersRaw] as Parameters<Task>,
          isInvalidate: config.isInvalidate,
          isPreProcess: config.isPreProcess,
          meta,
        },
        this
      );
    if ((isSameRequest && !config.isPreProcess) || config.isInvalidate)
      await this._config.defaultEventHandlers?.onFinally?.(
        {
          data,
          error,
          hash,
          parameters: [...parametersRaw] as Parameters<Task>,
          isInvalidate: config.isInvalidate,
          isPreProcess: config.isPreProcess,
          meta,
        },
        this
      );
  }

  protected get _isRetryOnError(): boolean {
    const retryOnError = this._config.retryOnError ?? 0;

    return retryOnError > 0 && this._retriedOnError < retryOnError;
  }

  protected _isSameRequest(hash: string) {
    return hash === this.requestDetails.value?.hash;
  }

  public reset() {
    this._setRequestDetails(() => this._defaultRequestDetails);
    this._retriedOnError = 0;

    return this;
  }
}

export type RequestConfigBase<
  Task extends (...args: any) => Promise<any>,
  TaskError = any,
  Meta = Record<string, any>
> = RequestEventHandlersType<Task, TaskError, Meta> & Partial<SharedConfigs>;

// export type RequestConfigs<
//   Task extends (...args: any) => Promise<any>,
//   TaskError = any
// > =
//   | ({ isInvalidate?: never } & RequestEventHandlersType<Task, TaskError>)
//   | ({ isInvalidate?: true } & Omit<
//       RequestEventHandlersType<Task, TaskError>,
//       "onCache"
//     >);

export type RequestConfigs<
  Task extends (...args: any) => Promise<any>,
  TaskError = any,
  Meta = Record<string, any>
> = Partial<SharedConfigs> & RequestEventHandlersType<Task, TaskError, Meta>;

export type RequestEventHandlersType<
  Task extends (...args: any) => Promise<any>,
  TaskError = any,
  Meta = Record<string, any>
> = {
  onCache?: (
    params: {
      cache?: TypeCacheRecord<Awaited<ReturnType<Task>> | undefined | null>;
      hash: string;
      parameters: Parameters<Task>;
      meta: Meta;
    } & SharedConfigs,
    instance: RequestCore<Task, TaskError, Meta>
  ) => any;

  onRequest?: (
    params: {
      parameters: Parameters<Task>;
      hash: string;
      meta: Meta;
    } & SharedConfigs,
    instance: RequestCore<Task, TaskError, Meta>
  ) => any;

  onSuccess?: (
    params: {
      data: Awaited<ReturnType<Task>> | undefined | null;
      hash: string;
      parameters: Parameters<Task>;
      meta: Meta;
    } & SharedConfigs,
    instance: RequestCore<Task, TaskError, Meta>
  ) => any | Promise<any>;

  onError?: (
    params: {
      error: TaskError;
      hash: string;
      parameters: Parameters<Task>;
      meta: Meta;
    } & SharedConfigs,
    instance: RequestCore<Task, TaskError, Meta>
  ) => any | Promise<any>;

  onFinally?: (
    props: {
      data?: Awaited<ReturnType<Task>> | undefined | null;
      error?: TaskError;
      hash: string;
      parameters: Parameters<Task>;
      meta: Meta;
    } & SharedConfigs,
    instance: RequestCore<Task, TaskError, Meta>
  ) => any | Promise<any>;
};

export type TaskManagerInterceptorsType<
  Task extends (...args: any) => Promise<any>,
  TaskError = any,
  Meta = Record<string, any>
> = {
  cache?: (
    props: {
      cache:
        | TypeCacheRecord<Awaited<ReturnType<Task>> | undefined | null>
        | undefined;
      hash: string;
      parameters: Parameters<Task>;
      config: RequestConfigBase<Task, TaskError, Meta>;
      meta: Meta;
    } & SharedConfigs,
    instance: RequestCore<Task, TaskError, Meta>
  ) => Promise<{
    isIgnored?: boolean;
    cache:
      | TypeCacheRecord<Awaited<ReturnType<Task>> | undefined | null>
      | undefined;
  }>;

  request?: (
    props: {
      parameters: Parameters<Task>;
      config: RequestConfigBase<Task, TaskError, Meta>;
      hash: string;
      meta: Meta;
    } & SharedConfigs,
    instance: RequestCore<Task, TaskError, Meta>
  ) => Promise<{
    isPrevented?: boolean;
    parameters: Parameters<Task>;
  }>;

  success?: (
    props: {
      parameters: Parameters<Task>;
      config: RequestConfigBase<Task, TaskError, Meta>;
      data: Awaited<ReturnType<Task>> | undefined | null;
      hash: string;
      meta: Meta;
    } & SharedConfigs,
    instance: RequestCore<Task, TaskError, Meta>
  ) => Promise<{
    isIgnored?: boolean;
    data: Awaited<ReturnType<Task>> | undefined | null;
  }>;

  error?: (
    props: {
      parameters: Parameters<Task>;
      config: RequestConfigBase<Task, TaskError, Meta>;
      error: TaskError;
      hash: string;
      meta: Meta;
    } & SharedConfigs,
    instance: RequestCore<Task, TaskError, Meta>
  ) => Promise<{
    isIgnored?: boolean;
    error: TaskError;
  }>;

  finally?: (
    props: {
      parameters: Parameters<Task>;
      config: RequestConfigBase<Task, TaskError, Meta>;
      data: Awaited<ReturnType<Task>> | undefined | null;
      error: TaskError | undefined;
      hash: string;
      meta: Meta;
    } & SharedConfigs,
    instance: RequestCore<Task, TaskError, Meta>
  ) => Promise<{
    isIgnored?: boolean;
    data?: Awaited<ReturnType<Task>> | undefined | null;
    error?: TaskError;
  }>;
};

export type SharedConfigs = {
  isPreProcess: boolean | undefined;
  isInvalidate: boolean | undefined;
};

export type TaskManagerCoreConfig<
  Task extends (...args: any) => Promise<any>,
  TaskError = any,
  Meta = Record<string, any>
> = {
  handler: Task;

  retryOnError?: number;
  retryOnErrorDelay?: number;
  preventNewRequestDuration?: number;

  defaultEventHandlers?: RequestEventHandlersType<Task, TaskError, Meta>;

  interceptors?: TaskManagerInterceptorsType<Task, TaskError, Meta>;

  meta?: Meta;

  cache?: CacheCoreConfig;
};

export type RequestDataRecordType<Task extends (...args: any) => Promise<any>> =
  {
    parameters?: Parameters<Task>;
    hash?: string;
    createdAt?: number;
    updatedAt?: number;
    isRequested?: boolean;
    isSucceed?: boolean;
    isCached?: boolean;
    isBlocked?: boolean;
    isFailed?: boolean;
    status?: RequestStatus;
    procedure?: RequestStatus[];
    error?: any;
    data?: Awaited<ReturnType<Task>> | null;
    cache?: TypeCacheRecord<Awaited<ReturnType<Task>> | undefined | null>;
  };

export type RequestStatus =
  | "requested"
  | "began"
  | "succeed"
  | "cached"
  | "blocked"
  | "failed";
